import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  Wallet, Landmark, Plus, Edit, Trash2, Banknote, Eye, EyeOff, Info, Search,
  RefreshCw, ArrowUpRight, ArrowDownRight, X, ShieldAlert
} from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import ProTable from '../ui-pro/ProTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const trDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '-');
const EMPTY_FORM = { name: '', type: 'bank', is_active: true, info_only: false, info_only_start: '', notes: '' };
const PERIODS = [
  { id: 'month', label: 'Bu Ay' },
  { id: '3m', label: 'Son 3 Ay' },
  { id: '6m', label: 'Son 6 Ay' },
  { id: '1y', label: 'Son 1 Yıl' },
  { id: 'all', label: 'Tüm Zamanlar' },
];

const CashAccountsTab = ({ canManage = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters] = useState({ start: '', end: '', type: 'all', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = period === 'custom'
        ? { period: 'custom', start: custom.start, end: custom.end }
        : { period };
      const res = await axios.get(`${API}/cash-accounts`, { params });
      setData(res.data);
      setSelected(prev => {
        const next = res.data.accounts.find(a => a.id === prev?.id);
        return next || res.data.accounts.find(a => a.key === 'nakit') || res.data.accounts[0] || null;
      });
      // İşlem listesi de aynı dönemle açılır
      setFilters(f => ({ ...f, start: res.data.period?.start || '', end: res.data.period?.end || '' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hesaplar yüklenemedi');
    } finally { setLoading(false); }
  }, [period, custom.start, custom.end]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async () => {
    if (!selected?.id) return;
    setDetailLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.search) params.search = filters.search;
      const res = await axios.get(`${API}/cash-accounts/${selected.id}/transactions`, { params });
      setDetail(res.data);
    } catch (e) {
      toast.error('İşlemler yüklenemedi');
    } finally { setDetailLoading(false); }
  }, [selected?.id, filters]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (acc) => {
    setEditing(acc);
    setForm({
      name: acc.name, type: acc.type, is_active: acc.is_active !== false,
      info_only: !!acc.info_only, info_only_start: acc.info_only_start || '', notes: acc.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Hesap adı zorunlu'); return; }
    try {
      const body = { ...form, name: form.name.trim(), info_only_start: form.info_only_start || null };
      if (editing) await axios.put(`${API}/cash-accounts/${editing.id}`, body);
      else await axios.post(`${API}/cash-accounts`, body);
      toast.success(editing ? 'Hesap güncellendi' : 'Hesap eklendi');
      setShowForm(false);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`"${acc.name}" hesabını silmek istiyor musunuz? İşlemi olan hesaplar silinemez.`)) return;
    try {
      await axios.delete(`${API}/cash-accounts/${acc.id}`);
      toast.success('Hesap silindi');
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  };

  const accounts = data?.accounts || [];
  const cashAccounts = accounts.filter(a => a.type === 'cash');
  const bankAccounts = accounts.filter(a => a.type === 'bank');

  const columns = useMemo(() => [
    { key: 'date', label: 'Tarih', sortable: true, width: 110, value: (r) => r.date, render: (r) => trDate(r.date) },
    {
      key: 'tx_type', label: 'Tür', sortable: true, width: 90, value: (r) => r.tx_type,
      render: (r) => (
        <Badge className={`text-[10px] ${r.tx_type === 'income' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'} hover:bg-current/10`}>
          {r.tx_type === 'income' ? 'Gelir' : 'Gider'}
        </Badge>
      ),
    },
    { key: 'title', label: 'Açıklama', sortable: true, render: (r) => (
      <div className="min-w-0">
        <p className="font-medium text-slate-800 truncate max-w-[340px]">{r.title || '-'}</p>
        {r.notes && <p className="text-[11px] text-slate-500 truncate max-w-[340px]">{r.notes}</p>}
      </div>
    ) },
    { key: 'category', label: 'Kategori', sortable: true, width: 130, render: (r) => (
      <span className="text-[12px] text-slate-600">{r.category || '-'}</span>
    ) },
    { key: 'link', label: 'Kaynak', width: 110, render: (r) => (
      r.organization_id ? <Badge className="text-[10px] bg-sky-100 text-[#176B91] hover:bg-sky-100">Organizasyon</Badge>
        : r.reservation_id ? <Badge className="text-[10px] bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Rezervasyon</Badge>
          : <span className="text-[11px] text-slate-400">Manuel</span>
    ) },
    {
      key: 'amount', label: 'Tutar', align: 'right', sortable: true, width: 130,
      value: (r) => (r.tx_type === 'income' ? r.amount : -r.amount),
      render: (r) => (
        <span className={`font-semibold tabular-nums ${r.tx_type === 'income' ? 'text-emerald-700' : 'text-rose-700'}`}>
          {r.tx_type === 'income' ? '' : '- '}{tl(r.amount)}
        </span>
      ),
    },
  ], []);

  return (
    <div className="space-y-4" data-testid="cash-accounts-tab">
      <PageHeader
        icon={Wallet}
        title="Kasalar"
        description="Kasa ve banka hesaplarının yönetimi, güncel bakiyeler ve detaylı işlem listesi"
        testid="cash-accounts-header"
        actions={
          <>
            <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="cash-accounts-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </Button>
            {canManage && (
              <Button size="sm" className="h-9 bg-[#0B172A] hover:bg-[#13233C]" onClick={openNew} data-testid="cash-account-new-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Yeni Hesap
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            {PERIODS.map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={`h-9 px-3 rounded-lg text-[13px] font-medium transition-colors ${period === p.id ? 'bg-[#0B172A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`cash-period-${p.id}`}>{p.label}</button>
            ))}
            <div className="flex items-center gap-1.5 ml-1 pl-2 border-l border-[#E2E8F0]">
              <Input type="date" value={custom.start} className="h-9 w-[142px]"
                onChange={(e) => { setCustom(c => ({ ...c, start: e.target.value })); setPeriod('custom'); }}
                data-testid="cash-period-custom-start" />
              <span className="text-slate-400 text-[12px]">–</span>
              <Input type="date" value={custom.end} className="h-9 w-[142px]"
                onChange={(e) => { setCustom(c => ({ ...c, end: e.target.value })); setPeriod('custom'); }}
                data-testid="cash-period-custom-end" />
            </div>
            <Badge className="ml-auto text-[11px] bg-sky-100 text-[#176B91] hover:bg-sky-100" data-testid="cash-period-label">
              {data?.period?.label || '-'}: {trDate(data?.period?.start) } – {trDate(data?.period?.end)}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
            <Info className="w-3.5 h-3.5 text-[#176B91]" />
            <span>
              "Bilgi amaçlı" işaretli hesaplar (Bostancı vb.) kasa ve banka toplamlarına <b>yansımaz</b>;
              işlemleri görünür ancak yalnızca takip amaçlıdır.
            </span>
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={ArrowUpRight} label={`${data?.period?.label || 'Dönem'} Girişi`} value={tl(data?.period?.income)} tone="emerald"
          sub="seçili dönem geliri" testid="stat-period-income" />
        <StatCard icon={ArrowDownRight} label={`${data?.period?.label || 'Dönem'} Çıkışı`} value={tl(data?.period?.expense)} tone="rose"
          sub="seçili dönem gideri" testid="stat-period-expense" />
        <StatCard icon={Banknote} label={`${data?.period?.label || 'Dönem'} Nakit Neti`} value={tl(data?.period?.cash_net)} tone="indigo"
          sub={`Banka neti ${tl(data?.period?.bank_net)}`} testid="stat-period-net" />
        <StatCard icon={EyeOff} label="Bilgi Amaçlı (Dönem)" value={tl(data?.period?.info_total)} tone="slate"
          sub="kasaya yansımayan hareket" testid="stat-info-total" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Banknote} label="Güncel Toplam Nakit" value={tl(data?.cash_total)} tone="emerald"
          sub={`${cashAccounts.length} kasa hesabı · kümülatif`} testid="stat-cash-total" />
        <StatCard icon={Landmark} label="Güncel Toplam Banka" value={tl(data?.bank_total)} tone="sky"
          sub={`${bankAccounts.filter(a => !a.info_only).length} banka hesabı (bilgi amaçlı hariç)`} testid="stat-bank-total" />
        <StatCard icon={Wallet} label="Toplam Varlık" value={tl(data?.grand_total)} tone="indigo"
          sub="nakit + banka (kümülatif)" testid="stat-grand-total" />
        <StatCard icon={EyeOff} label="Bilgi Amaçlı (Toplam)" value={tl(data?.info_total)} tone="slate"
          sub="tüm zamanlar" testid="stat-info-cumulative" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {accounts.map(acc => {
          const isSel = selected?.id === acc.id;
          return (
            <div key={acc.id}
              className={`bg-white rounded-xl border shadow-sm transition-all ${isSel ? 'border-[#176B91] ring-1 ring-[#176B91]/30' : 'border-[#E2E8F0] hover:border-slate-300'}`}
              data-testid={`cash-account-card-${acc.key}`}>
              <button onClick={() => setSelected(acc)} className="w-full text-left px-4 pt-4 pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`rounded-lg p-2 ${acc.type === 'cash' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-[#176B91]'}`}>
                      {acc.type === 'cash' ? <Banknote className="w-4 h-4" /> : <Landmark className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{acc.name}</p>
                      <p className="text-[11px] text-slate-500">{acc.type === 'cash' ? 'Kasa' : 'Banka'} · {acc.tx_count} toplam işlem</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {acc.is_active === false && <Badge className="text-[10px] bg-slate-100 text-slate-600 hover:bg-slate-100">Pasif</Badge>}
                    {acc.info_only && <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">Bilgi amaçlı</Badge>}
                  </div>
                </div>
                <div className="mt-3 flex items-end justify-between gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                      {data?.period?.label || 'Dönem'} neti
                    </p>
                    <p className={`text-xl font-bold tabular-nums ${acc.period_net < 0 ? 'text-rose-700' : 'text-slate-900'}`}
                      data-testid={`cash-account-period-net-${acc.key}`}>
                      {acc.period_net > 0 ? '+' : ''}{tl(acc.period_net)}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Güncel bakiye <b className={acc.kasa_balance < 0 ? 'text-rose-700' : 'text-slate-700'}>{tl(acc.kasa_balance)}</b>
                    </p>
                  </div>
                  {acc.info_only && (
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Bilgi amaçlı</p>
                      <p className="text-[13px] font-semibold text-amber-700 tabular-nums">{tl(acc.period_info_amount)}</p>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1 text-emerald-700"><ArrowUpRight className="w-3 h-3" />{tl(acc.period_income)}</span>
                  <span className="inline-flex items-center gap-1 text-rose-700"><ArrowDownRight className="w-3 h-3" />{tl(acc.period_expense)}</span>
                  <span className="ml-auto">{acc.period_tx_count} işlem</span>
                </div>
              </button>
              {canManage && (
                <div className="flex items-center gap-1.5 px-4 pb-3 pt-1 border-t border-[#E2E8F0]">
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => openEdit(acc)}
                    data-testid={`cash-account-edit-${acc.key}`}>
                    <Edit className="w-3 h-3 mr-1" /> Düzenle
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-[11px] text-rose-700 hover:bg-rose-50"
                    onClick={() => remove(acc)} data-testid={`cash-account-delete-${acc.key}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Sil
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-[11px] ml-auto text-[#176B91]"
                    onClick={() => setSelected(acc)} data-testid={`cash-account-view-${acc.key}`}>
                    <Eye className="w-3 h-3 mr-1" /> İşlemler
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selected && (
        <>
          <PageHeader
            icon={selected.type === 'cash' ? Banknote : Landmark}
            title={`${selected.name} — İşlem Listesi`}
            description={selected.info_only
              ? 'Bu hesap bilgi amaçlıdır; hareketleri kasa/banka toplamlarına yansımaz'
              : 'Hesaba işlenmiş tüm gelir ve gider kayıtları'}
            testid="cash-account-detail-header"
          >
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Başlangıç</Label>
                <Input type="date" value={filters.start} className="h-9 w-[150px]"
                  onChange={(e) => setFilters(f => ({ ...f, start: e.target.value }))}
                  data-testid="account-filter-start" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Bitiş</Label>
                <Input type="date" value={filters.end} className="h-9 w-[150px]"
                  onChange={(e) => setFilters(f => ({ ...f, end: e.target.value }))}
                  data-testid="account-filter-end" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Tür</Label>
                <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-9 w-[130px]" data-testid="account-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="income">Gelir</SelectItem>
                    <SelectItem value="expense">Gider</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[11px] text-slate-500">Ara</Label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={filters.search} placeholder="Açıklama veya not" className="h-9 pl-8"
                    onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                    data-testid="account-filter-search" />
                </div>
              </div>
              <Button variant="outline" size="sm" className="h-9"
                onClick={() => setFilters({ start: '', end: '', type: 'all', search: '' })}
                data-testid="account-filter-clear">
                <X className="w-3.5 h-3.5 mr-1" /> Temizle
              </Button>
            </div>
          </PageHeader>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={ArrowUpRight} label="Filtreli Gelir" value={tl(detail?.summary?.income_total)} tone="emerald" testid="account-sum-income" />
            <StatCard icon={ArrowDownRight} label="Filtreli Gider" value={tl(detail?.summary?.expense_total)} tone="rose" testid="account-sum-expense" />
            <StatCard icon={Wallet} label="Filtreli Net" value={tl(detail?.summary?.net)} tone="indigo" testid="account-sum-net" />
            <StatCard icon={Landmark} label="Hesap Bakiyesi" value={tl(selected.kasa_balance)} tone="slate"
              sub={selected.info_only ? 'bilgi amaçlı tutar hariç' : 'güncel'} testid="account-sum-balance" />
          </div>

          <ProTable
            testid="account-tx-table"
            columns={columns}
            rows={detail?.transactions || []}
            initialSort={{ key: 'date', dir: 'desc' }}
            emptyText={detailLoading ? 'Yükleniyor...' : 'Bu filtrelerle işlem bulunamadı'}
            emptyIcon={Wallet}
          />
        </>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="cash-account-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Hesabı Düzenle' : 'Yeni Kasa / Banka Hesabı'}</DialogTitle>
            <DialogDescription>
              Hesap anahtarı değişmez; mevcut gelir-gider kayıtlarının bağlantısı korunur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px]">Hesap Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Örn: Ziraat Bankası" data-testid="account-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="account-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Kasa (Nakit)</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Durum</Label>
                <Select value={form.is_active ? 'active' : 'passive'}
                  onValueChange={(v) => setForm(f => ({ ...f, is_active: v === 'active' }))}>
                  <SelectTrigger data-testid="account-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="passive">Pasif (yeni işlemde seçilemez)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button type="button" onClick={() => setForm(f => ({ ...f, info_only: !f.info_only }))}
              className={`w-full flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors ${form.info_only ? 'border-amber-300 bg-amber-50' : 'border-[#E2E8F0] hover:bg-slate-50'}`}
              data-testid="account-form-info-only">
              <ShieldAlert className={`w-4 h-4 mt-0.5 ${form.info_only ? 'text-amber-700' : 'text-slate-400'}`} />
              <div>
                <p className="text-[13px] font-semibold text-slate-800">Bilgi amaçlı hesap (kasaya yansımaz)</p>
                <p className="text-[11px] text-slate-500">
                  İşlem girilebilir ve listelenir, ancak kesim tarihinden itibaren kasa/banka toplamlarına dahil edilmez.
                </p>
              </div>
            </button>
            {form.info_only && (
              <div>
                <Label className="text-[12px]">Kasa dışı başlangıç tarihi</Label>
                <Input type="date" value={form.info_only_start}
                  onChange={(e) => setForm(f => ({ ...f, info_only_start: e.target.value }))}
                  data-testid="account-form-info-start" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Boş bırakılırsa 09.10.2026 kullanılır. Bu tarihten önceki hareketler toplamlarda aynen kalır.
                </p>
              </div>
            )}
            <div>
              <Label className="text-[12px]">Not</Label>
              <Textarea value={form.notes} rows={2}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="account-form-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowForm(false)} data-testid="account-form-cancel">İptal</Button>
              <Button className="bg-[#0B172A] hover:bg-[#13233C]" onClick={save} data-testid="account-form-save">
                {editing ? 'Güncelle' : 'Hesap Ekle'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashAccountsTab;
